import { Link } from 'react-router-dom';
import { Wrench, Zap, Droplets, Wind, Shield, Clock, Phone, CheckCircle, Building, Hammer, Paintbrush, TreePine, AlertTriangle, Sparkles } from 'lucide-react';

function FacilityMaintenance() {
  const mainServices = [
    {
      icon: Wind,
      title: "HVAC Services",
      description: "Complete heating, ventilation, and air conditioning solutions to maintain optimal indoor comfort and air quality for your facility.",
      services: [
        "Preventive maintenance programs",
        "Emergency repair services 24/7",
        "System installation & replacement",
        "Ductwork cleaning & repair",
        "Thermostat & control systems",
        "Energy efficiency audits",
        "Refrigerant management",
        "Indoor air quality solutions"
      ]
    },
    {
      icon: Zap,
      title: "Electrical Services",
      description: "Licensed electrical services from routine maintenance to complex system upgrades, ensuring safety and code compliance.",
      services: [
        "Lighting installation & retrofit",
        "Panel upgrades & maintenance",
        "Emergency & backup power systems",
        "Electrical troubleshooting",
        "Code compliance inspections",
        "Energy-efficient LED conversions",
        "Outlet & circuit installation",
        "Generator maintenance & testing"
      ]
    },
    {
      icon: Droplets,
      title: "Plumbing Services",
      description: "Comprehensive plumbing solutions from minor repairs to major system overhauls, keeping your facility running smoothly.",
      services: [
        "Leak detection & repair",
        "Drain cleaning & maintenance",
        "Water heater service & replacement",
        "Backflow prevention & testing",
        "Fixture installation & repair",
        "Sewer line maintenance",
        "Water pressure optimization",
        "Grease trap maintenance"
      ]
    },
    {
      icon: Building,
      title: "Roofing Services",
      description: "Protect your investment with professional roofing services from routine inspections to emergency repairs and full replacements.",
      services: [
        "Roof inspections & assessments",
        "Leak detection & repair",
        "Preventive maintenance programs",
        "Emergency tarping & repair",
        "Gutter cleaning & repair",
        "Flat roof & membrane systems",
        "Skylight maintenance",
        "Roof coating applications"
      ]
    },
    {
      icon: Hammer,
      title: "General Repairs & Carpentry",
      description: "Skilled tradespeople for all general repairs, from minor fixes to major renovations that keep your facility in top condition.",
      services: [
        "Door & window repairs",
        "Drywall patching & repair",
        "Ceiling tile replacement",
        "Cabinet & millwork repairs",
        "Hardware replacement",
        "Lock & key services",
        "ADA compliance modifications",
        "Structural repairs"
      ]
    },
    {
      icon: Paintbrush,
      title: "Painting & Finishes",
      description: "Professional painting and finishing services to maintain your facility's appearance and protect surfaces from wear.",
      services: [
        "Interior painting",
        "Exterior painting",
        "Epoxy floor coatings",
        "Wall covering installation",
        "Wood staining & refinishing",
        "Pressure washing prep",
        "Texture matching & repair",
        "Anti-graffiti coatings"
      ]
    },
    {
      icon: TreePine,
      title: "Exterior & Grounds",
      description: "Complete exterior maintenance to enhance curb appeal, ensure safety, and protect your property value.",
      services: [
        "Parking lot maintenance",
        "Striping & signage",
        "Landscaping & irrigation",
        "Snow & ice removal",
        "Pressure washing",
        "Fence & gate repairs",
        "Exterior lighting",
        "Sidewalk & curb repair"
      ]
    },
    {
      icon: Shield,
      title: "Safety & Fire Systems",
      description: "Critical safety system maintenance to protect your people, property, and ensure regulatory compliance.",
      services: [
        "Fire suppression inspection",
        "Sprinkler system maintenance",
        "Fire extinguisher service",
        "Emergency lighting testing",
        "Exit sign maintenance",
        "Fire alarm system service",
        "Kitchen hood systems",
        "Safety equipment inspections"
      ]
    },
    {
      icon: Sparkles,
      title: "Janitorial & Deep Cleaning",
      description: "Professional cleaning services from daily janitorial to specialized deep cleaning for a healthy, welcoming environment.",
      services: [
        "Daily janitorial services",
        "Floor stripping & waxing",
        "Carpet cleaning & extraction",
        "Window cleaning",
        "Restroom sanitization",
        "Post-construction cleanup",
        "Pressure washing",
        "Specialty surface cleaning"
      ]
    }
  ];

  const serviceModels = [
    {
      icon: Clock,
      title: "24/7 Emergency Response",
      description: "Round-the-clock availability for urgent repairs. Our dispatch team is ready to mobilize skilled technicians anytime, anywhere. We understand that facility emergencies don't wait for business hours.",
      features: ["Average 2-hour response time", "Nationwide technician network", "Direct emergency hotline", "Real-time status updates"]
    },
    {
      icon: CheckCircle,
      title: "Preventive Maintenance Programs",
      description: "Customized maintenance schedules designed to prevent problems before they occur. Regular inspections and service extend equipment life and reduce costly emergency repairs.",
      features: ["Tailored maintenance calendars", "Equipment lifecycle tracking", "Priority scheduling", "Budget-friendly pricing"]
    },
    {
      icon: Phone,
      title: "Single Point of Contact",
      description: "One call handles it all. Your dedicated account manager coordinates all trades and services, eliminating the hassle of managing multiple vendors for different maintenance needs.",
      features: ["Dedicated account manager", "Consolidated invoicing", "Simplified communication", "Vendor coordination"]
    },
    {
      icon: Building,
      title: "Multi-Location Management",
      description: "Consistent service quality across all your locations. We standardize maintenance protocols and reporting so you can compare performance across your entire portfolio.",
      features: ["Standardized procedures", "Portfolio-wide reporting", "Consistent pricing", "National coverage"]
    }
  ];

  const industries = [
    { name: "Retail", icon: "🏪" },
    { name: "Restaurants & Hospitality", icon: "🍽️" },
    { name: "Healthcare Facilities", icon: "🏥" },
    { name: "Office Buildings", icon: "🏢" },
    { name: "Industrial & Warehouse", icon: "🏭" },
    { name: "Financial Institutions", icon: "🏦" },
    { name: "Educational Facilities", icon: "🎓" },
    { name: "Government Buildings", icon: "🏛️" },
    { name: "Multi-Family Residential", icon: "🏘️" },
    { name: "Convenience Stores", icon: "⛽" },
    { name: "Hotels", icon: "🏨" }
  ];

  return (
    <div className="bg-black min-h-screen pt-28">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black"></div>
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-gold/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-32">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gold/20 border border-gold/40 rounded-full mb-8">
            <Wrench className="w-10 h-10 text-gold" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Facility <span className="text-gold">Maintenance Services</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            Comprehensive maintenance solutions to keep your facilities operating at peak performance. 
            From routine upkeep to emergency repairs, we handle every aspect of facility maintenance 
            so you can focus on your core business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider">
              Request Service
            </Link>
            <a href="tel:9184933418" className="inline-flex items-center justify-center bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-10 rounded text-lg transition-all uppercase tracking-wider">
              <Phone className="w-5 h-5 mr-2" />
              (918) 493-3418
            </a>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Your Complete <span className="text-gold">Maintenance Partner</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              Williams Diversified provides full-service facility maintenance for commercial, industrial, 
              and institutional properties nationwide. Our skilled technicians, comprehensive service offerings, 
              and commitment to rapid response ensure your facilities remain safe, functional, and 
              professionally maintained. Whether you need a single repair or an ongoing maintenance program, 
              we deliver consistent quality with every service call.
            </p>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold text-gold mb-2">24/7</div>
              <div className="text-gray-400 text-sm">Emergency Response</div>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold text-gold mb-2">50+</div>
              <div className="text-gray-400 text-sm">States Served</div>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold text-gold mb-2">100%</div>
              <div className="text-gray-400 text-sm">Licensed & Insured</div>
            </div>
            <div className="bg-black/50 border border-gold/20 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold text-gold mb-2">1</div>
              <div className="text-gray-400 text-sm">Call Does It All</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Comprehensive <span className="text-gold">Service Categories</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From mechanical systems to cosmetic repairs, we provide every service your facility needs under one roof
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.map((service, index) => (
              <div key={index} className="bg-black/60 border border-gold/20 rounded-lg overflow-hidden hover:border-gold/40 transition-all group">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gold/20 border border-gold/40 rounded-lg flex items-center justify-center mr-4 group-hover:bg-gold/30 transition-all">
                      <service.icon className="w-6 h-6 text-gold" />
                    </div>
                    <h3 className="text-xl font-bold text-gold">{service.title}</h3>
                  </div>
                  <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.services.map((item, idx) => (
                      <li key={idx} className="flex items-start text-gray-300 text-sm">
                        <span className="text-gold mr-2 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Models Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              How We <span className="text-gold">Deliver Service</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Flexible service models designed to meet your specific facility needs and budget
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {serviceModels.map((model, index) => (
              <div key={index} className="bg-black/50 border border-gold/20 rounded-lg p-8 hover:border-gold/40 transition-all">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mr-4">
                    <model.icon className="w-7 h-7 text-gold" />
                  </div>
                  <h3 className="text-xl font-bold text-gold">{model.title}</h3>
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {model.description}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {model.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4 text-gold mr-2 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h3 className="text-3xl font-bold text-white mb-12 font-serif text-center" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Our Maintenance <span className="text-gold">Process</span>
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">1</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Request Service</h4>
              <p className="text-gray-400 text-sm">Call, email, or submit online. Describe your issue and we'll dispatch the right technician.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">2</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Rapid Dispatch</h4>
              <p className="text-gray-400 text-sm">A qualified technician is assigned and scheduled based on priority and location.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">3</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Professional Service</h4>
              <p className="text-gray-400 text-sm">Our technician diagnoses, repairs, and documents the work with photos and notes.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gold text-2xl font-bold">4</span>
              </div>
              <h4 className="text-gold font-bold mb-2">Complete Reporting</h4>
              <p className="text-gray-400 text-sm">Detailed service reports delivered with recommendations for future maintenance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Industries <span className="text-gold">We Serve</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Our maintenance expertise spans across diverse facility types with industry-specific knowledge
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {industries.map((industry, index) => (
              <div key={index} className="bg-black/50 border border-gold/20 rounded-lg p-4 text-center hover:border-gold/40 transition-all hover:bg-gold/5">
                <div className="text-3xl mb-2">{industry.icon}</div>
                <div className="text-gray-300 text-sm font-medium">{industry.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="bg-gradient-to-r from-gold/20 via-gold/10 to-gold/20 py-16 relative overflow-hidden border-t border-b border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/30 border border-gold rounded-full mb-6">
            <AlertTriangle className="w-8 h-8 text-gold" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            24/7 <span className="text-gold">Emergency Service</span>
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Facility emergencies don't wait for business hours. Our emergency dispatch team is standing by 
            around the clock to get a qualified technician to your location fast.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:9184933418" className="inline-flex items-center justify-center bg-gold hover:bg-gold-dark text-black font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider">
              <Phone className="w-5 h-5 mr-2" />
              Call Now: (918) 493-3418
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              The Williams Diversified <span className="text-gold">Advantage</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black/60 border border-gold/20 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-gold" />
              </div>
              <h3 className="text-xl font-bold text-gold mb-4">Licensed & Insured</h3>
              <p className="text-gray-400">
                All work performed by licensed professionals with comprehensive insurance coverage. 
                Full compliance with local codes and regulations guaranteed.
              </p>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-gold" />
              </div>
              <h3 className="text-xl font-bold text-gold mb-4">Quality Guaranteed</h3>
              <p className="text-gray-400">
                We stand behind our work with satisfaction guarantees. If something isn't right, 
                we'll make it right at no additional cost.
              </p>
            </div>
            <div className="bg-black/60 border border-gold/20 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-gold/20 border border-gold/40 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-8 h-8 text-gold" />
              </div>
              <h3 className="text-xl font-bold text-gold mb-4">National Reach</h3>
              <p className="text-gray-400">
                Consistent service quality from coast to coast. One partner for all your locations 
                with standardized procedures and pricing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting pointer-events-none"></div>
        <div className="absolute inset-0 geometric-brass pointer-events-none"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready to Simplify Your <span className="text-gold">Facility Maintenance</span>?
          </h2>
          <p className="text-gray-300 text-xl mb-10 max-w-2xl mx-auto">
            Contact us today to discuss your maintenance needs and learn how Williams Diversified 
            can become your single-source facility maintenance partner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider">
              Get Started Today
            </Link>
            <a href="mailto:inquire@williamsdiversified.com" className="inline-flex items-center justify-center bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-5 px-12 rounded text-lg transition-all uppercase tracking-wider">
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FacilityMaintenance;
